import { BACKEND_BASE_URL } from "../config/env";
import { getTrackingFingerprintHeaders } from "./trackingFingerprint";

const TRACKING_CLIENT_SESSION_KEY = "cekresi_tracking_client_session";
const TRACKING_CLIENT_SESSION_EXPIRES_KEY = "cekresi_tracking_client_session_expires";
const TOKEN_PATTERN = /^v1\.[a-f0-9]{32}\.[0-9]{10}\.[A-Za-z0-9_-]{43}$/;
const REFRESH_SKEW_SECONDS = 10 * 60;

let inFlightSessionRequest = null;

function isGoogleAdsCrawler() {
  if (typeof navigator === "undefined") {
    return false;
  }
  return navigator.userAgent.trim().toLowerCase() === "mediapartners-google";
}

function storedSessionIsValid(token, expiresAt) {
  if (!token || !TOKEN_PATTERN.test(token)) {
    return false;
  }
  const expires = Number(expiresAt);
  if (!Number.isFinite(expires) || expires <= 0) {
    return false;
  }
  return Date.now() / 1000 < expires - REFRESH_SKEW_SECONDS;
}

function readStoredSession() {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    const token = window.localStorage.getItem(TRACKING_CLIENT_SESSION_KEY);
    const expiresAt = window.localStorage.getItem(TRACKING_CLIENT_SESSION_EXPIRES_KEY);
    if (storedSessionIsValid(token, expiresAt)) {
      return token;
    }
    window.localStorage.removeItem(TRACKING_CLIENT_SESSION_KEY);
    window.localStorage.removeItem(TRACKING_CLIENT_SESSION_EXPIRES_KEY);
  } catch {
    return "";
  }
  return "";
}

function storeSession(token, expiresAt) {
  if (typeof window === "undefined" || !storedSessionIsValid(token, expiresAt)) {
    return;
  }

  try {
    window.localStorage.setItem(TRACKING_CLIENT_SESSION_KEY, token);
    window.localStorage.setItem(TRACKING_CLIENT_SESSION_EXPIRES_KEY, String(expiresAt));
  } catch {
    // Browsers can deny localStorage; the caller can still use this token for the current request.
  }
}

export function clearTrackingClientSession() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(TRACKING_CLIENT_SESSION_KEY);
    window.localStorage.removeItem(TRACKING_CLIENT_SESSION_EXPIRES_KEY);
  } catch {
    // Ignore storage errors; the next request can still ask the backend for a fresh token.
  }
}

async function requestTrackingClientSession() {
  if (!BACKEND_BASE_URL) {
    return "";
  }
  const fingerprintHeaders = await getTrackingFingerprintHeaders();

  const res = await fetch(`${BACKEND_BASE_URL}/tracking/session`, {
    method: "POST",
    headers: fingerprintHeaders,
  });
  if (!res.ok) {
    return "";
  }

  const payload = await res.json();
  const token = typeof payload?.client_session === "string" ? payload.client_session : "";
  const expiresAt = payload?.expires_at;
  if (!storedSessionIsValid(token, expiresAt)) {
    return "";
  }

  storeSession(token, expiresAt);
  return token;
}

export async function getTrackingClientSession() {
  // The backend gives verified Mediapartners-Google traffic a cache-only path.
  // Avoid consuming the shared-IP session limiter before that request arrives.
  if (isGoogleAdsCrawler()) {
    return "";
  }

  const existing = readStoredSession();
  if (existing) {
    return existing;
  }

  if (!inFlightSessionRequest) {
    inFlightSessionRequest = requestTrackingClientSession().finally(() => {
      inFlightSessionRequest = null;
    });
  }

  try {
    return await inFlightSessionRequest;
  } catch {
    return "";
  }
}
